import Client from "./client";
import {pageParams} from "./paging";

/**
 * One page of a project's support queue, newest first.
 *
 * `filters` ({status, open, owner, needsAction}) are applied by the server, not
 * by the queue on the page it holds - filtering a page in the browser shows the
 * matching tickets of page one instead of the first page of matching tickets,
 * and silently loses everything past the boundary. "me" as the owner resolves
 * against the authenticated caller server-side.
 *
 * The response also carries an `entitlement`, because support ticket management
 * is a Pro feature but an existing queue stays readable on any plan (see GET
 * /projects/:id/support-tickets). `total` comes back for the first page of a
 * given set of filters only.
 */
export const GetSupportTickets = ({projectId, accessToken, filters, limit, cursor}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/support-tickets`, {
            params: pageParams({filters, limit, cursor}),
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve({
                items: res.data.supportTickets,
                nextCursor: res.data.nextCursor,
                hasMore: res.data.hasMore,
                total: res.data.total,
                entitlement: res.data.entitlement,
                // The distinct support levels on this project's tickets, for
                // the queue's level filter. First page only, like `total`.
                levels: res.data.levels,
            });
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

export const GetSupportTicket = ({supportTicketId, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/support-tickets/${supportTicketId}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.supportTicket);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// The same support ticket, addressed by this project's own number rather
// than its database id - see Tasks.js's GetTicketByNumber for why.
export const GetSupportTicketByNumber = ({projectId, supportTicketNumber, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.get(`/api/projects/${projectId}/support-tickets/number/${supportTicketNumber}`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.supportTicket);
        }).catch((e) => {
            reject(e.response?.data || { error: "Something went wrong. Please try again." });
        })
    })
}

// Rejects with `status` attached as well as the body: a 402 here means the
// project's plan doesn't include support tickets, which the caller offers an
// upgrade for rather than reporting as an error.
export const CreateSupportTicket = ({projectId, subject, body, requesterEmail, requesterName, accessToken}) => {
    return new Promise((resolve, reject) => {
        Client.post(`/api/projects/${projectId}/support-tickets`, {subject, body, requesterEmail, requesterName}, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.supportTicket);
        }).catch((e) => {
            reject({
                ...(e.response?.data || { error: "Something went wrong. Please try again." }),
                status: e.response?.status,
            });
        })
    })
}

// `version`/`base` are how the API tells a save that touched something else
// changed in the meantime from a real conflict - see PUT /support-tickets/:id,
// and UpdateBug, which works the same way. `base` only needs the fields
// actually being sent, as this client originally loaded them.
export const UpdateSupportTicket = ({supportTicketId, subject, body, requesterEmail, requesterName, assignedToId, ticketId, bugId, status, open, version, base, accessToken}) => {
    const data = {};
    if (subject !== undefined) data.subject = subject;
    if (body !== undefined) data.body = body;
    if (requesterEmail !== undefined) data.requesterEmail = requesterEmail;
    if (requesterName !== undefined) data.requesterName = requesterName;
    // null is meaningful here - it unassigns - so this can't collapse to a
    // falsy check.
    if (assignedToId !== undefined) data.assignedToId = assignedToId;
    // null unlinks, so these can't collapse to a falsy check either.
    if (ticketId !== undefined) data.ticketId = ticketId;
    if (bugId !== undefined) data.bugId = bugId;
    if (status !== undefined) data.status = status;
    if (open !== undefined) data.open = open;
    if (version !== undefined) data.version = version;
    if (base !== undefined) data.base = base;

    return new Promise((resolve, reject) => {
        Client.put(`/api/support-tickets/${supportTicketId}`, data, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }).then((res) => {
            resolve(res.data.supportTicket);
        }).catch((e) => {
            reject({
                ...(e.response?.data || { error: "Something went wrong. Please try again." }),
                status: e.response?.status,
            });
        })
    })
}
